import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import LoadingSpinner from "../LoadingSpinner";

const AuditLog = ({ onMessage, onLoading }) => {
  const { user } = useAuth();
  const [auditEvents, setAuditEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    eventType: "all",
    dateRange: "30d",
    status: "all",
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [isExporting, setIsExporting] = useState(false);

  const eventTypes = [
    { value: "all", label: "All Events" },
    { value: "login", label: "Login" },
    { value: "logout", label: "Logout" },
    { value: "password_change", label: "Password Change" },
    { value: "mfa_enable", label: "MFA Enable" },
    { value: "mfa_disable", label: "MFA Disable" },
    { value: "session_revoke", label: "Session Revoke" },
    { value: "profile_update", label: "Profile Update" },
    { value: "failed_login", label: "Failed Login" },
  ];

  const dateRanges = [
    { value: "7d", label: "Last 7 days" },
    { value: "30d", label: "Last 30 days" },
    { value: "90d", label: "Last 90 days" },
    { value: "1y", label: "Last year" },
  ];

  useEffect(() => {
    loadAuditLog();
  }, [filters, pagination.page]);

  const loadAuditLog = async () => {
    setIsLoading(true);
    try {
      // TODO: Implement actual API call
      // const response = await authAPI.getAuditLog({
      //   ...filters,
      //   page: pagination.page,
      //   limit: pagination.limit,
      // });

      // Mock audit data
      const mockEvents = generateMockAuditEvents();
      const filteredEvents = filterMockEvents(mockEvents);

      setAuditEvents(
        filteredEvents.slice(
          (pagination.page - 1) * pagination.limit,
          pagination.page * pagination.limit,
        ),
      );
      setPagination((prev) => ({
        ...prev,
        total: filteredEvents.length,
        totalPages: Math.ceil(filteredEvents.length / pagination.limit),
      }));
    } catch (error) {
      onMessage({
        type: "error",
        text: "Failed to load security audit log",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const generateMockAuditEvents = () => {
    const events = [];
    const now = new Date();

    // Generate mock events for the last 30 days
    for (let i = 0; i < 100; i++) {
      const eventDate = new Date(
        now - Math.random() * 30 * 24 * 60 * 60 * 1000,
      );
      const eventTypes = [
        "login",
        "logout",
        "password_change",
        "mfa_enable",
        "profile_update",
        "failed_login",
        "session_revoke",
      ];
      const eventType =
        eventTypes[Math.floor(Math.random() * eventTypes.length)];
      const statuses = ["success", "failed", "warning"];
      const status =
        eventType === "failed_login"
          ? "failed"
          : statuses[Math.floor(Math.random() * statuses.length)];

      events.push({
        id: `event-${i}`,
        eventType,
        status,
        timestamp: eventDate.toISOString(),
        ip: `192.168.1.${Math.floor(Math.random() * 255)}`,
        location: [
          "San Francisco, CA",
          "New York, NY",
          "London, UK",
          "Tokyo, JP",
        ][Math.floor(Math.random() * 4)],
        userAgent:
          "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36",
        device: ["MacBook Pro", "iPhone 14", "Windows PC", "Android Phone"][
          Math.floor(Math.random() * 4)
        ],
        details: getEventDetails(eventType, status),
      });
    }

    return events.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  };

  const getEventDetails = (eventType, status) => {
    const details = {
      login:
        status === "success"
          ? "User logged in successfully"
          : "Login attempt failed - invalid credentials",
      logout: "User logged out",
      password_change:
        status === "success"
          ? "Password changed successfully"
          : "Password change failed",
      mfa_enable: "Multi-factor authentication enabled",
      mfa_disable: "Multi-factor authentication disabled",
      session_revoke: "Session revoked by user",
      profile_update: "Profile information updated",
      failed_login: "Failed login attempt",
    };

    return details[eventType] || "Security event";
  };

  const filterMockEvents = (events) => {
    return events.filter((event) => {
      // Filter by event type
      if (
        filters.eventType !== "all" &&
        event.eventType !== filters.eventType
      ) {
        return false;
      }

      // Filter by status
      if (filters.status !== "all" && event.status !== filters.status) {
        return false;
      }

      // Filter by date range
      const eventDate = new Date(event.timestamp);
      const now = new Date();
      const daysAgo = parseInt(filters.dateRange);
      const cutoffDate = new Date(now - daysAgo * 24 * 60 * 60 * 1000);

      if (eventDate < cutoffDate) {
        return false;
      }

      return true;
    });
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const exportAuditLog = async () => {
    setIsExporting(true);
    try {
      // TODO: Implement actual API call
      // const response = await authAPI.exportAuditLog(filters);

      // Mock CSV generation
      const headers = [
        "Timestamp",
        "Event Type",
        "Status",
        "IP Address",
        "Location",
        "Device",
        "Details",
      ];
      const csvContent = [
        headers.join(","),
        ...auditEvents.map((event) =>
          [
            new Date(event.timestamp).toLocaleString(),
            event.eventType,
            event.status,
            event.ip,
            `"${event.location}"`,
            `"${event.device}"`,
            `"${event.details}"`,
          ].join(","),
        ),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `security-audit-log-${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      onMessage({
        type: "success",
        text: "Audit log exported successfully",
      });
    } catch (error) {
      onMessage({
        type: "error",
        text: "Failed to export audit log",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const getEventIcon = (eventType, status) => {
    const icons = {
      login: status === "success" ? "✅" : "❌",
      logout: "🚪",
      password_change: "🔑",
      mfa_enable: "🛡️",
      mfa_disable: "⚠️",
      session_revoke: "🚫",
      profile_update: "👤",
      failed_login: "❌",
    };

    return icons[eventType] || "📋";
  };

  const getStatusColor = (status) => {
    const colors = {
      success: "text-green-600 bg-green-50 border-green-200",
      failed: "text-red-600 bg-red-50 border-red-200",
      warning: "text-yellow-600 bg-yellow-50 border-yellow-200",
    };

    return colors[status] || "text-gray-600 bg-gray-50 border-gray-200";
  };

  const formatEventType = (eventType) => {
    return eventType
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">
            Security Audit Log
          </h3>
          <p className="mt-1 text-sm text-gray-600">
            Monitor security events and access to your account
          </p>
        </div>
        <button
          onClick={exportAuditLog}
          disabled={isExporting}
          className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors text-sm disabled:opacity-50 flex items-center space-x-2"
        >
          {isExporting ? (
            <>
              <LoadingSpinner size="sm" />
              <span>Exporting...</span>
            </>
          ) : (
            <>
              <span>📥</span>
              <span>Export CSV</span>
            </>
          )}
        </button>
      </div>

      {/* Filters */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Event Type
            </label>
            <select
              value={filters.eventType}
              onChange={(e) => handleFilterChange("eventType", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            >
              {eventTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date Range
            </label>
            <select
              value={filters.dateRange}
              onChange={(e) => handleFilterChange("dateRange", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            >
              {dateRanges.map((range) => (
                <option key={range.value} value={range.value}>
                  {range.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange("status", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Statuses</option>
              <option value="success">Success</option>
              <option value="failed">Failed</option>
              <option value="warning">Warning</option>
            </select>
          </div>
        </div>
      </div>

      {/* Audit Events */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {auditEvents.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Event
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      When
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Location
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Device
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {auditEvents.map((event) => (
                    <tr key={event.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className="text-xl mr-3">
                            {getEventIcon(event.eventType, event.status)}
                          </span>
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {formatEventType(event.eventType)}
                            </div>
                            <div className="text-sm text-gray-500">
                              {event.details}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(event.status)}`}
                        >
                          {event.status.charAt(0).toUpperCase() +
                            event.status.slice(1)}
                        </span>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div>
                          {new Date(event.timestamp).toLocaleDateString()}
                        </div>
                        <div className="text-xs">
                          {new Date(event.timestamp).toLocaleTimeString()}
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center">
                          <span className="mr-1">📍</span>
                          <span>{event.location}</span>
                        </div>
                        <div className="text-xs mt-1">{event.ip}</div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {event.device}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() =>
                      setPagination((prev) => ({
                        ...prev,
                        page: prev.page - 1,
                      }))
                    }
                    disabled={pagination.page <= 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() =>
                      setPagination((prev) => ({
                        ...prev,
                        page: prev.page + 1,
                      }))
                    }
                    disabled={pagination.page >= pagination.totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing{" "}
                      <span className="font-medium">
                        {(pagination.page - 1) * pagination.limit + 1}
                      </span>{" "}
                      to{" "}
                      <span className="font-medium">
                        {Math.min(
                          pagination.page * pagination.limit,
                          pagination.total,
                        )}
                      </span>{" "}
                      of <span className="font-medium">{pagination.total}</span>{" "}
                      results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() =>
                          setPagination((prev) => ({
                            ...prev,
                            page: prev.page - 1,
                          }))
                        }
                        disabled={pagination.page <= 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        ←
                      </button>
                      {[...Array(Math.min(5, pagination.totalPages))].map(
                        (_, i) => {
                          const page = i + 1;
                          return (
                            <button
                              key={page}
                              onClick={() =>
                                setPagination((prev) => ({ ...prev, page }))
                              }
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                pagination.page === page
                                  ? "z-10 bg-primary-50 border-primary-500 text-primary-600"
                                  : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                              }`}
                            >
                              {page}
                            </button>
                          );
                        },
                      )}
                      <button
                        onClick={() =>
                          setPagination((prev) => ({
                            ...prev,
                            page: prev.page + 1,
                          }))
                        }
                        disabled={pagination.page >= pagination.totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        →
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <span className="text-4xl mb-4 block">📋</span>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No events found
            </h3>
            <p className="text-gray-600">
              No security events match your current filters.
            </p>
          </div>
        )}
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center">
            <span className="text-2xl mr-3">✅</span>
            <div>
              <p className="text-xl font-bold text-gray-900">
                {auditEvents.filter((e) => e.status === "success").length}
              </p>
              <p className="text-sm text-gray-600">Successful</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center">
            <span className="text-2xl mr-3">❌</span>
            <div>
              <p className="text-xl font-bold text-gray-900">
                {auditEvents.filter((e) => e.status === "failed").length}
              </p>
              <p className="text-sm text-gray-600">Failed</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center">
            <span className="text-2xl mr-3">🚪</span>
            <div>
              <p className="text-xl font-bold text-gray-900">
                {auditEvents.filter((e) => e.eventType === "login").length}
              </p>
              <p className="text-sm text-gray-600">Logins</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center">
            <span className="text-2xl mr-3">🌍</span>
            <div>
              <p className="text-xl font-bold text-gray-900">
                {new Set(auditEvents.map((e) => e.location)).size}
              </p>
              <p className="text-sm text-gray-600">Locations</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditLog;
